import type { User } from '../api';
import { LimitBar } from './LimitBar';
import { Icon, type IconName } from '../icons';

export type View = 'ideas' | 'board' | 'map' | 'timeline' | 'agent' | 'duplicates' | 'review' | 'archive' | 'settings';

/** Navigation in two groups (round 2 canvas; D-84 V1: Duplicates is its own item under Review). */
const NAV_GROUPS: { label: string; items: { id: View; label: string; icon: IconName }[] }[] = [
  {
    label: 'Library',
    items: [
      { id: 'ideas', label: 'Ideas', icon: 'ideas' },
      { id: 'board', label: 'Board', icon: 'board' },
      { id: 'map', label: 'Mind map', icon: 'map' },
      { id: 'timeline', label: 'Timeline', icon: 'timeline' },
      { id: 'agent', label: 'Ask Claude', icon: 'chat' },
    ],
  },
  {
    label: 'Review',
    items: [
      { id: 'review', label: 'Triage', icon: 'triage' },
      { id: 'duplicates', label: 'Duplicates', icon: 'dupes' },
      { id: 'archive', label: 'Archive', icon: 'archive' },
    ],
  },
];

export function Sidebar({
  currentView,
  onViewChange,
  user,
  onCapture,
  onSearch,
  collapsed,
  onToggleCollapse,
  duplicateCount = 0,
  reviewCount = 0,
  agentView = false,
}: {
  currentView: View;
  onViewChange: (view: View) => void;
  user: User | null;
  onCapture: () => void;
  /** Opens search (the ⌘K box once P3-06 lands). */
  onSearch: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  duplicateCount?: number;
  reviewCount?: number;
  /** The Agent view is parked behind the `agent_view` flag (P1-14). */
  agentView?: boolean;
}) {
  return (
    <aside className={`sidebar${collapsed ? ' sidebar-collapsed' : ''}`} aria-label="Sidebar">
      <div className="sb-head">
        {!collapsed && (
          <a className="sb-wordmark" href="/app" onClick={(e) => { e.preventDefault(); onViewChange('ideas'); }}>
            <span>neural</span><span className="sb-wordmark-repo">repo</span>
          </a>
        )}
        {onToggleCollapse && (
          <button
            className="sb-icon-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name="collapse" />
          </button>
        )}
      </div>

      <div className="sb-actions">
        {/* Collapsed, the icon needs a name; expanded, the visible words are the name (keys are hints). */}
        <button className="sb-new" onClick={onCapture} title="New idea (C)" aria-label={collapsed ? 'New idea' : undefined} aria-keyshortcuts="c">
          <Icon name="add" size={16} />
          {!collapsed && <><span className="sb-grow">New idea</span><kbd className="sb-hint" aria-hidden="true">C</kbd></>}
        </button>
        <button className="sb-search" onClick={onSearch} title="Search (⌘K)" aria-label={collapsed ? 'Search or jump to' : undefined} aria-keyshortcuts="Meta+K Control+K">
          <Icon name="search" size={16} />
          {!collapsed && <><span className="sb-grow">Search or jump to…</span><kbd className="sb-kbd" aria-hidden="true">⌘K</kbd></>}
        </button>
      </div>

      <nav className="sb-nav" aria-label="Main">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="sb-group">
            {!collapsed && <span className="sb-group-label">{group.label}</span>}
            {group.items.filter((item) => item.id !== 'agent' || agentView).map((item) => {
              const active = currentView === item.id;
              const count =
                item.id === 'ideas' ? user?.active_idea_count ?? 0
                : item.id === 'review' ? reviewCount
                : item.id === 'duplicates' ? duplicateCount
                : 0;
              return (
                <button
                  key={item.id}
                  className={`sb-item${active ? ' sb-item-active' : ''}`}
                  onClick={() => onViewChange(item.id)}
                  aria-current={active ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon name={item.icon} />
                  {!collapsed && <span className="sb-grow">{item.label}</span>}
                  {!collapsed && user?.plan === 'free' && (item.id === 'agent' || item.id === 'duplicates') && (
                    <span className="sb-pro">Pro</span>
                  )}
                  {!collapsed && count > 0 && (
                    <span className={`sb-count${item.id === 'review' ? ' sb-count-strong' : ''}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="sb-grow" />

      {!collapsed && user?.is_trialing && (
        <div className="sb-plan">
          <span className="sb-plan-row">
            <span className="sb-plan-name">Pro trial</span>
            <span className={`sb-plan-meta${user.trial_days_remaining != null && user.trial_days_remaining <= 7 ? ' sb-plan-warn' : ''}`}>
              {user.trial_days_remaining} days left
            </span>
          </span>
          <a href="/upgrade" className="sb-plan-link">Choose a plan</a>
        </div>
      )}
      {!collapsed && user?.plan === 'free' && !user.is_trialing && (
        <LimitBar ideaCount={user.active_idea_count} limit={50} />
      )}

      {user && (
        <div className="sb-user">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="sb-avatar" />
          ) : (
            <span className="sb-avatar" aria-hidden="true">{(user.display_name ?? user.email)[0]?.toUpperCase()}</span>
          )}
          {!collapsed && (
            <span className="sb-user-text">
              <span className="sb-user-name">{user.display_name ?? user.email}</span>
              <span className="sb-user-plan">{user.plan === 'pro' ? 'Pro' : user.is_trialing ? 'Pro trial' : 'Free'}</span>
            </span>
          )}
          {!collapsed && (
            <>
              <button
                className={`sb-icon-btn${currentView === 'settings' ? ' sb-icon-btn-active' : ''}`}
                onClick={() => onViewChange('settings')}
                aria-label="Settings"
                title="Settings"
              >
                <Icon name="settings" />
              </button>
              <a className="sb-icon-btn" href="/auth/logout" aria-label="Sign out" title="Sign out">
                <Icon name="signout" />
              </a>
            </>
          )}
        </div>
      )}
    </aside>
  );
}
